import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PeopleIcon from '@mui/icons-material/People';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  projectEssentialApi,
  type GlsLandSaleItem,
  type LandInfo,
  type ProjectDetail as ProjectDetailType,
} from '../services/api';
import ProjectResourcesDialog from '../components/ProjectResourcesDialog';
import { ProjectCoreTeamDialog } from '../components/ProjectCoreTeamDialog';
import ProjectAnnouncementsDialog from '../components/ProjectAnnouncementsDialog';
import { logProjectResourcesClicked } from '../services/analytics';
import {
  canOpenProjectMaterials,
  projectResourcesLabel,
} from '../utils/materialsLabels';
import { INITIAL_DOMAIN_URL, isDevelopment, isProduction } from '../../config/env';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

// ─── LandInfo formatters ────────────────────────────────────────────
// Duplicated from the old GlsDetail page (now deleted) so the Land
// info tab on the project detail page renders the same field formats
// users were already familiar with.

function formatSqft(sqft: number | null): string | null {
  if (sqft == null) return null;
  return `${sqft.toLocaleString()} sqft`;
}

function formatLandPrice(price: number | null): string | null {
  if (price == null) return null;
  if (price >= 1_000_000_000) return `$${(price / 1_000_000_000).toFixed(2)}B`;
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(2)}M`;
  return `$${price.toLocaleString()}`;
}

function formatAwardDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCompleted(completeDate: string | null, topDate: number | null): string | null {
  if (completeDate) {
    const d = new Date(completeDate);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  }
  if (topDate != null) {
    return String(topDate);
  }
  return null;
}

// Renders the "Land info" tab body — same field order the deprecated
// GlsDetail page used so users moving from a GLS link to the unified
// project detail see the same information in the same order.
function LandInfoPanel({ landInfo }: { landInfo: LandInfo }) {
  const locationLine =
    [landInfo.region, landInfo.district, landInfo.planningArea]
      .filter((s): s is string => Boolean(s && s.trim()))
      .join(' | ') || landInfo.location || null;
  const housingUnits =
    landInfo.housingUnits != null ? landInfo.housingUnits.toLocaleString() : null;
  const awarded = formatAwardDate(landInfo.awardDate);
  const siteArea = formatSqft(landInfo.siteAreaSqft);
  const landPrice = formatLandPrice(landInfo.successfulTenderPrice);
  // PSF PPR — "Price per square foot per plot ratio". Pre-computed in
  // the source column, so we just format it as a dollar-per-sqft figure
  // (typical magnitude is $1,000–$1,500/sqft, so no need for the M/B
  // bucketing that `formatLandPrice` does above).
  const psfPpr =
    landInfo.grossPlotRatioSqft != null
      ? `$${landInfo.grossPlotRatioSqft.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} psf`
      : null;

  return (
    <Stack divider={<Divider flexItem sx={{ borderColor: 'var(--color-border)' }} />}>
      <DetailRow label="Site Name" value={landInfo.siteName || '—'} />
      <DetailRow label="Developer" value={landInfo.developer || '—'} />
      <DetailRow label="Estimated Units" value={housingUnits || '—'} />
      <DetailRow label="Awarded Date" value={awarded || '—'} />
      <DetailRow label="Location" value={locationLine || '—'} />
      <DetailRow label="Nearby MRT" value={landInfo.nearbyMrtStation || '—'} />
      {/* Tenure first; fall back to LeasePeriod (e.g. "99 yrs Leasehold")
          which is more reliably populated on GLS rows. */}
      <DetailRow label="Tenure" value={landInfo.tenure || landInfo.leasePeriod || '—'} />
      <DetailRow label="Site Area" value={siteArea || '—'} />
      <DetailRow label="Land Price" value={landPrice || '—'} />
      <DetailRow label="PSF PPR" value={psfPpr || '—'} />
      <DetailRow label="URA Estimated TOP" value={landInfo.uraExpectedTop || '—'} />
    </Stack>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '110px 1fr', sm: '150px 1fr' },
        gap: { xs: 1, sm: 2 },
        py: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
          fontSize: '0.7rem',
          alignSelf: 'center',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'var(--color-text-strong)', fontWeight: 500, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function ProjectDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [coreTeamOpen, setCoreTeamOpen] = useState(false);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [landSale, setLandSale] = useState<GlsLandSaleItem | null>(null);
  // Detail-page tab. The "Land info" tab is only rendered (and only
  // selectable) when the backend returned a joined ProjectLandSale
  // row on `project.landInfo`. When that row goes away on a refetch
  // we fall back to 'project' automatically via the effect below.
  const [activeTab, setActiveTab] = useState<'project' | 'land'>('project');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .getProjectDetail(id)
      .then((res) => {
        if (mounted) setProject(res);
      })
      .catch((err) => {
        if (mounted) setError(err?.message || 'Failed to load project');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id) {
      setLandSale(null);
      return;
    }
    let mounted = true;
    projectEssentialApi
      .getLandSaleByProjectId(id)
      .then((res) => {
        if (mounted) setLandSale(res);
      })
      .catch(() => {
        if (mounted) setLandSale(null);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id) {
      setAnnouncementCount(0);
      return;
    }
    let mounted = true;
    projectEssentialApi
      .getProjectAnnouncements(id)
      .then((res) => {
        if (mounted) setAnnouncementCount(res.total ?? res.items?.length ?? 0);
      })
      .catch(() => {
        if (mounted) setAnnouncementCount(0);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  // If the joined ProjectLandSale row disappears (e.g. the user
  // navigates to a project without one), reset the active tab so we
  // don't end up showing an empty/hidden panel. Pure local state
  // bookkeeping — no fetches.
  useEffect(() => {
    if (!project?.landInfo && activeTab === 'land') {
      setActiveTab('project');
    }
  }, [project?.landInfo, activeTab]);

  const completed = project ? formatCompleted(project.completeDate, project.topDate) : null;
  const locationLine = project
    ? [project.area, project.district || null]
        .filter(Boolean)
        .join(' • ') ||
      project.region ||
      project.address ||
      null
    : null;

  const superPickerUrl = project
    ? `${isDevelopment ? window.location.origin : 'https://transformation.huttonsgroup.com'}/super-picker?projectId=${encodeURIComponent(project.id)}&from=project-essential`
    : '';

  // Production links go through htns.sg so company SSO runs first; it forwards
  // `redirect` and `projectId`, which ProjectEssentialApp turns into this page.
  const shareUrl = project
    ? `${isProduction ? INITIAL_DOMAIN_URL : window.location.origin}/project-essential?redirect=details&projectId=${encodeURIComponent(project.id)}`
    : '';

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — no-op.
    }
  };

  return (
    <Box>
      {/* Back navigation */}
      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        size="small"
        sx={{
          mb: { xs: 1.5, sm: 2 },
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1.5,
          borderColor: 'var(--color-border-strong)',
          color: 'var(--color-text-strong)',
          bgcolor: 'var(--color-surface)',
          px: 1.5,
          py: 0.5,
          '&:hover': {
            borderColor: 'var(--color-accent)',
            bgcolor: 'var(--color-accent-soft)',
            color: 'var(--color-accent-strong)',
          },
        }}
      >
        Back
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : !project ? (
        <Typography sx={{ color: 'var(--color-text-muted)' }}>Project not found.</Typography>
      ) : (
        <Card
          sx={{
            bgcolor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--color-shadow-card)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Featured image — mirrors the super-picker-public detail banner styling */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: { xs: '16 / 10', sm: '16 / 9' },
                maxHeight: { md: 520 },
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                mb: { xs: 2, sm: 2.5 },
              }}
            >
              {project.coverImage ? (
                <Box
                  component="img"
                  src={project.coverImage}
                  alt={project.name || project.id}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  No image available
                </Box>
              )}
            </Box>

            {/* Title + identity */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: 'var(--color-text-strong)',
                      lineHeight: 1.2,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    {project.name || project.id}
                  </Typography>
                  <Tooltip title={linkCopied ? 'Link copied' : 'Copy link'}>
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      aria-label="Copy link to this project"
                      sx={{
                        flexShrink: 0,
                        color: linkCopied ? '#22c55e' : 'var(--color-text-muted)',
                        '&:hover': { color: 'var(--color-accent-strong)', bgcolor: 'var(--color-accent-soft)' },
                      }}
                    >
                      {linkCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
                {project.developer && (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'var(--color-text-muted)', mt: 0.5, fontWeight: 500 }}
                  >
                    by {project.developer}
                  </Typography>
                )}
              </Box>
              <Stack spacing={1} alignItems="stretch" sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  startIcon={<CampaignOutlinedIcon />}
                  onClick={() => setAnnouncementsOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: 'var(--color-accent)',
                    color: '#fff',
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: 'var(--color-accent-strong)',
                    },
                  }}
                >
                  Announcements ({announcementCount})
                </Button>
                {project.categoryId !== 'C1804'
                  && project.categoryId !== 'C1807'
                  && project.country === 'SG' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    href={superPickerUrl}
                    onClick={(e) => {
                      // Always stay in the current browser tab. When PE is
                      // embedded in an iframe, navigate the top window so we
                      // don't load Super Picker inside the iframe.
                      e.preventDefault();
                      const url = superPickerUrl;
                      if (!url) return;
                      try {
                        if (window.top && window.top !== window.self) {
                          window.top.location.href = url;
                          return;
                        }
                      } catch {
                        // Cross-origin top access — fall through.
                      }
                      window.location.href = url;
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: 'var(--color-accent)',
                      color: 'var(--color-accent-strong)',
                      fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                      px: { xs: 1.5, sm: 2 },
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: 'var(--color-accent-strong)',
                        bgcolor: 'var(--color-accent-soft)',
                      },
                    }}
                  >
                    View in Super Picker
                  </Button>
                )}
              </Stack>
            </Stack>
            {/* Quick-glance chips */}
            {project.projectType && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}
              >
                <Chip
                  label={project.projectType}
                  sx={{
                    bgcolor: 'var(--color-accent-soft)',
                    color: 'var(--color-accent-strong)',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            )}

            {/* Location row */}
            {locationLine && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                  {locationLine}
                </Typography>
              </Stack>
            )}

            {/* Resources CTA — opens the shared ProjectResourcesDialog */}
            <Button
              onClick={() => {
                if (!project?.id || !canOpenProjectMaterials(project.hasMaterials)) return;
                logProjectResourcesClicked(project.id);
                setResourcesOpen(true);
              }}
              variant="contained"
              startIcon={<FolderOpenOutlinedIcon />}
              fullWidth
              disableElevation
              disabled={!canOpenProjectMaterials(project?.hasMaterials)}
              sx={{
                mt: { xs: 2, sm: 2.5 },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                py: { xs: 1, sm: 1.1 },
                borderRadius: 1.5,
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                '&:hover': { bgcolor: 'var(--color-accent-strong)' },
              }}
            >
              {projectResourcesLabel(project?.hasMaterials)}
            </Button>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 1.5, width: '100%' }}
            >
              {!project?.name?.toLowerCase().includes('gls') && (
                <Button
                  onClick={() => setCoreTeamOpen(true)}
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  sx={{
                    flex: { xs: '0 0 auto', sm: 1 },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: { xs: 1, sm: 1.1 },
                    borderRadius: 1.5,
                    color: 'var(--color-accent-strong)',
                    borderColor: 'var(--color-accent)',
                    '&:hover': {
                      borderColor: 'var(--color-accent-strong)',
                      bgcolor: 'var(--color-accent-soft)',
                    },
                  }}
                >
                  Project Taggers
                </Button>
              )}
              {project.meetingLink && (
                <Button
                  component="a"
                  href={project.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  disableElevation
                  sx={{
                    flex: { xs: '0 0 auto', sm: 1 },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: { xs: 1, sm: 1.1 },
                    px: { xs: 1.25, sm: 1.75 },
                    borderRadius: 1.5,
                    bgcolor: '#25D366',
                    color: '#fff',
                    whiteSpace: { xs: 'nowrap', sm: 'normal' },
                    '&:hover': { bgcolor: '#1DA851' },
                  }}
                >
                  Hyper Group Chat
                </Button>
              )}
              {landSale && (
                <Button
                  onClick={() => {
                    navigate(
                      `/project-essential/gls-land-sales/${encodeURIComponent(String(landSale.id))}`,
                    );
                  }}
                  variant="outlined"
                  startIcon={<MapOutlinedIcon />}
                  sx={{
                    flex: { xs: '0 0 auto', sm: 1 },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: { xs: 1, sm: 1.1 },
                    borderRadius: 1.5,
                    color: 'var(--color-accent-strong)',
                    borderColor: 'var(--color-accent)',
                    '&:hover': {
                      borderColor: 'var(--color-accent-strong)',
                      bgcolor: 'var(--color-accent-soft)',
                    },
                  }}
                >
                  View Land Report
                </Button>
              )}
            </Stack>

            {/* Tab strip — always shows "Project"; the "Land info"
                tab only mounts when the backend returned a joined
                ProjectLandSale row. Sits flush above the detail rows
                so the rows feel like a tab body rather than a free-
                floating list. */}
            <Box
              sx={{
                mt: { xs: 2, sm: 2.5 },
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v as 'project' | 'land')}
                variant="standard"
                sx={{
                  minHeight: 38,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    minHeight: 38,
                    px: 1.5,
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: 'var(--color-accent)',
                  },
                  '& .Mui-selected': {
                    color: 'var(--color-accent-strong) !important',
                  },
                }}
              >
                <Tab label="Project" value="project" />
                {project.landInfo && <Tab label="Land info" value="land" />}
              </Tabs>
            </Box>

            {/* Tab body — render the corresponding panel. The Stack
                wrappers keep both panels visually identical in
                spacing/divider treatment, so toggling tabs feels like
                swapping content within the same frame. */}
            <Box sx={{ pt: { xs: 1, sm: 1.5 } }}>
              {activeTab === 'project' ? (
                <Stack divider={<Divider flexItem sx={{ borderColor: 'var(--color-border)' }} />}>
                  <DetailRow label="Project Name" value={project.name || project.id} />
                  <DetailRow label="Developer" value={project.developer || '—'} />
                  <DetailRow label="Tenure" value={project.tenure || '—'} />
                  <DetailRow label="Total Units" value={project.totalUnits || '—'} />
                  <DetailRow label="District" value={project.district || '—'} />
                  {/* Note: the DB labels these fields `area` and `region`, but
                      the values are swapped relative to what the UI calls them.
                      Render `region` under the "Area" label and vice versa
                      rather than renaming the API/DB columns. */}
                  <DetailRow label="Area" value={project.region || '—'} />
                  <DetailRow label="Region" value={project.area || '—'} />
                  <DetailRow label="Expected TOP" value={completed || '—'} />
                  {project.address && <DetailRow label="Address" value={project.address} />}
                </Stack>
              ) : project.landInfo ? (
                <LandInfoPanel landInfo={project.landInfo} />
              ) : null}
            </Box>

          </Box>
        </Card>
      )}

      {project && (
        <ProjectResourcesDialog
          open={resourcesOpen}
          onClose={() => setResourcesOpen(false)}
          projectId={project.id}
          projectName={project.name}
        />
      )}
      {project && (
        <ProjectCoreTeamDialog
          open={coreTeamOpen}
          onClose={() => setCoreTeamOpen(false)}
          projectId={project.id}
          projectName={project.name}
        />
      )}
      {project && (
        <ProjectAnnouncementsDialog
          open={announcementsOpen}
          onClose={() => setAnnouncementsOpen(false)}
          projectId={project.id}
          projectName={project.name}
        />
      )}
    </Box>
  );
}
